import { canEnterFromRoomSession, canWaitInLobbyFromRoomSession } from "../../utils/rtc-sfu-room-session";

export const ROOM_ENTRY_FAILURE_KIND = {
  ACCESS_DENIED: "access_denied",
  BROWSER_PERMISSIONS: "browser_permissions",
  CAPACITY_FULL: "capacity_full",
  ENTITLEMENT_AUTHORITY_UNAVAILABLE: "entitlement_authority_unavailable",
  NETWORK_ERROR: "network_error",
  RATE_LIMITED: "rate_limited",
  SERVICE_UNAVAILABLE: "service_unavailable",
  UNKNOWN: "unknown"
};

const CAPACITY_DENIED_REASONS = new Set([
  "active_capacity_full",
  "capacity_limit_reached",
  "room_full",
  "max_ccu_reached"
]);

function hasStatus(source, status) {
  return Number(source?.status) === status;
}

function isDashboardStatusError(source) {
  return source?.name === "DashboardApiError" || Number.isFinite(Number(source?.status));
}

function retryAfterValue(source) {
  const retryAfter = Number(source?.retryAfter);

  return Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : null;
}

function isNetworkError(source) {
  return source?.name === "TypeError" || source?.kind === ROOM_ENTRY_FAILURE_KIND.NETWORK_ERROR;
}

function isCapacityDeniedSession(source) {
  if (!source) {
    return false;
  }

  const reason =
    source.reason ||
    source.error ||
    source.code ||
    source.payload?.reason ||
    source.payload?.error ||
    source.payload?.code;

  if (hasStatus(source, 409) && CAPACITY_DENIED_REASONS.has(reason)) {
    return true;
  }

  if (canEnterFromRoomSession(source)) {
    return false;
  }

  return !reason || CAPACITY_DENIED_REASONS.has(reason);
}

function isEntitlementAuthorityUnavailable(source) {
  return (
    source?.code === ROOM_ENTRY_FAILURE_KIND.ENTITLEMENT_AUTHORITY_UNAVAILABLE ||
    source?.payload?.code === ROOM_ENTRY_FAILURE_KIND.ENTITLEMENT_AUTHORITY_UNAVAILABLE
  );
}

export function normalizeRoomEntryFailure(source, { config = {} } = {}) {
  if (isCapacityDeniedSession(source)) {
    const canLookAround = canWaitInLobbyFromRoomSession({ canEnter: false }, config);

    return {
      kind: ROOM_ENTRY_FAILURE_KIND.CAPACITY_FULL,
      titleId: "room-entry-modal.room-full-title",
      title: "Active participant limit reached",
      messageId: canLookAround ? "room-entry-modal.room-full" : "room-entry-modal.room-full-no-look-around",
      message: canLookAround
        ? "You can still look around while you wait for a spot."
        : "Please try again later when an active participant spot opens.",
      canLookAround,
      retryable: false,
      showDialog: false
    };
  }

  if (hasStatus(source, 429)) {
    const retryAfter = retryAfterValue(source);

    return {
      kind: ROOM_ENTRY_FAILURE_KIND.RATE_LIMITED,
      titleId: "room-entry-error.rate-limited-title",
      title: "Too many entry attempts",
      messageId: retryAfter
        ? "room-entry-error.rate-limited-message-with-retry-after"
        : "room-entry-error.rate-limited-message",
      message: retryAfter
        ? "Please wait {retryAfter} seconds before trying to enter again."
        : "Please wait a moment before trying to enter again.",
      values: retryAfter ? { retryAfter } : undefined,
      retryable: false,
      showDialog: true
    };
  }

  if (hasStatus(source, 401) || hasStatus(source, 403)) {
    return {
      kind: ROOM_ENTRY_FAILURE_KIND.ACCESS_DENIED,
      titleId: "room-entry-error.access-denied-title",
      title: "Unable to enter the Verse",
      messageId: "room-entry-error.access-denied-message",
      message: "You do not have permission to enter this Verse.",
      retryable: false,
      showDialog: true
    };
  }

  if (hasStatus(source, 503) && isEntitlementAuthorityUnavailable(source)) {
    return {
      kind: ROOM_ENTRY_FAILURE_KIND.ENTITLEMENT_AUTHORITY_UNAVAILABLE,
      titleId: "room-entry-error.entitlement-authority-unavailable-title",
      title: "Subscription limits unavailable",
      messageId: "room-entry-error.entitlement-authority-unavailable-message",
      message: "We couldn't verify the limits for this Verse. Please try again in a moment.",
      retryable: true,
      showDialog: true
    };
  }

  if (isDashboardStatusError(source) && Number(source.status) >= 500) {
    return {
      kind: ROOM_ENTRY_FAILURE_KIND.SERVICE_UNAVAILABLE,
      titleId: "room-entry-error.service-unavailable-title",
      title: "Verse unavailable",
      messageId: "room-entry-error.service-unavailable-message",
      message: "The Verse is temporarily unavailable. Please try again in a moment.",
      retryable: true,
      showDialog: true
    };
  }

  if (isNetworkError(source)) {
    return {
      kind: ROOM_ENTRY_FAILURE_KIND.NETWORK_ERROR,
      titleId: "room-entry-error.network-title",
      title: "Something went wrong",
      messageId: "room-entry-error.network-message",
      message: "We couldn't open the Verse. Check your connection and try again.",
      retryable: true,
      showDialog: true
    };
  }

  return {
    kind: ROOM_ENTRY_FAILURE_KIND.BROWSER_PERMISSIONS,
    titleId: "ui-root.entering-error",
    title: "Unable to Enter the Verse",
    messageId: "ui-root.entering-error-message",
    message:
      "We couldn't connect you to the Verse. This is often due to browser permission issues. Please follow these steps:",
    showPermissionSteps: true,
    retryable: true,
    showDialog: true
  };
}
